import React, { useEffect, useRef, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import PlusIconWhite from '../../images/PlusIconWhite';
import CloseIcon from '../../images/CloseIcon';
import EditIconV2 from '../../images/EditIconV2';
import { deleteAppVersion, listAppVersions } from '../../services/appVersion.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import AppVersionIcon from '../../images/AppVersionIcon';
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
  t,
} from '../../components/list';

/** Rollout scope codes -> i18n keys. */
const ROLLOUT_OPTIONS = [
  { value: 'ALL', name: 'All locations' },
  { value: 'PILOT', name: 'Pilot' },
  { value: 'SELECTED', name: 'Selected locations' },
];

/** The API returns snake_case or camelCase depending on the build; tolerate both. */
interface AppVersionRow {
  id: string;
  appName: string;
  releaseName: string;
  releaseNumber: string;
  rolloutScope: string;
}

const pickRow = (each: any): AppVersionRow => ({
  id: each.id,
  appName: each.appName ?? each.app_name ?? '-',
  releaseName: each.releaseName ?? each.release_name ?? '-',
  releaseNumber: each.releaseNumber ?? each.release_number ?? '-',
  rolloutScope: each.rolloutScope ?? each.rollout_scope ?? '-',
});

const rolloutLabel = (raw: string, language: any) => {
  const option = ROLLOUT_OPTIONS.find(o => o.value === raw);
  return option ? t(language, option.name, option.name) : String(raw);
};

/** The app name a till sends decides which platform a release reaches. */
const PLATFORM_NAMES: Record<string, string> = {
  'finex-pos': 'Windows',
  'finex-pos-android': 'Android',
};

const platformLabel = (appName: string, language: any) => {
  const name = PLATFORM_NAMES[String(appName ?? '').trim()];
  return name ? t(language, name, name) : '-';
};

const AppVersion = () => {
  const preLanguage = localStorage.getItem('language');
  const { isMobile } = useResponsive();
  const listRef = useRef<ListScreenHandle>(null);
  const [language, setLanguage] = useState<any>(null);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>('');
  const inforTable: any = {
    img: <AppVersionIcon />,
    title:
      language && language?.['No app versions yet']
        ? language?.['No app versions yet']
        : 'No app versions yet',
    path: '/appVersion/new',
    desc:
      language && language?.['Assign and manage the pos app versions here.']
        ? language?.['Assign and manage the pos app versions here.']
        : 'Assign and manage the pos app versions here.',
    titleCreate:
      language && language?.['Add app version'] ? language?.['Add app version'] : 'Add app version',
  };

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /**
   * GET /api/inventory/app-versions ignores page / limit / searchValue and
   * returns the whole list, so the page is sliced here. There is no search
   * param on the endpoint, hence no search box.
   */
  const fetchAppVersions = async (query: ListQuery) => {
    const response: any = await listAppVersions({});
    const raw = response?.data?.data ?? response?.data ?? [];
    const list: AppVersionRow[] = (Array.isArray(raw) ? raw : []).map(pickRow);
    const start = (query.page - 1) * query.limit;
    return {
      rows: list.slice(start, start + query.limit),
      count: response?.data?.count ?? list.length,
    };
  };

  const goToAppVersion = (id: string) => webNavigate('/' + preLanguage + '/appVersion/' + id);

  const handleDeleteEmployee = async () => {
    try {
      await deleteAppVersion(idDelete);
    } catch (error) {
      console.error('Error deleting app version:', error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<AppVersionRow>[] = [
    {
      key: 'appName',
      label: 'App name',
      width: 220,
      hideable: false,
      render: cells.primary(a => a.appName),
      mobile: 'title',
      onClick: a => goToAppVersion(String(a.id)),
    },
    {
      key: 'platform',
      label: 'Platform',
      width: 120,
      value: (a, ctx) => platformLabel(a.appName, ctx.language),
      mobile: 'sheet',
    },
    { key: 'releaseName', label: 'Release name', width: 220, mobile: 'subtitle' },
    { key: 'releaseNumber', label: 'Release number', width: 140, mobile: 'trailing' },
    {
      key: 'rolloutScope',
      label: 'Rollout scope',
      width: 180,
      value: (a, ctx) => rolloutLabel(a.rolloutScope, ctx.language),
      mobile: 'sheet',
    },
  ];

  // Delete stays unreachable from the list, as it was with the shared table
  // (the /appVersion/ branch hid it); the dialog below is kept as-is.
  const rowActions: ListRowAction<AppVersionRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: a => goToAppVersion(String(a.id)),
    },
  ];

  return (
    <>
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? "auto" : undefined,
          paddingBottom: isMobile ? "24px" : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: '#222525' }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.['App versions'] ? language?.['App versions'] : 'App versions'}
          </p>
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: '8px',
              padding: '8px 24px 8px 16px',
              backgroundColor: '#0F7679',
              borderRadius: '30px',
              width: 'fit-content',
              marginTop: isMobile ? '20px' : '0px',
            }}
            onClick={() => {
              webNavigate('/' + preLanguage + '/appVersion/new');
            }}
          >
            <div
              style={{ width: '24px', height: '24px' }}
              className="flex items-center justify-center"
            >
              <PlusIconWhite />
            </div>
            <p
              style={{ color: '#FFFFFF' }}
              className="text-[14px] leading-[130%] font-medium font-inter"
            >
              {language && language?.['Add app version']
                ? language?.['Add app version']
                : 'Add app version'}
            </p>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchAppVersions}
          search={false}
          rowActions={rowActions}
          countNoun="app versions"
          emptyState={inforTable}
        />
      </div>
      <div
        className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{
          backgroundColor: '#22252552',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100%',
        }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            maxWidth: 'calc(100vw - 32px)',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-center justify-between">
            <p
              style={{ fontSize: '20px' }}
              className="font-semibold font-inter text-main leading-[24px]"
            >
              {language && language?.['Delete app version?']
                ? language?.['Delete app version?']
                : 'Delete app version?'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
            <p
              className="text-[14px] font-inter leading-[24px]"
              style={{ color: '#6A7878' }}
            >
              {language &&
              language?.[
                'This app version will be permanently deleted. Are you sure you want to continue?'
              ]
                ? language?.[
                  'This app version will be permanently deleted. Are you sure you want to continue?'
                ]
                : 'This app version will be permanently deleted. Are you sure you want to continue?'}
            </p>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </div>
            <div
              onClick={handleDeleteEmployee}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppVersion;
